import React, { useState } from 'react';
import { Alert, Box, Button, Container, Grid, Paper, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../services/apiClient';
import logo from '../../assets/logo.svg';
import PsychologyIcon from '@mui/icons-material/Psychology';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InsightsIcon from '@mui/icons-material/Insights';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShieldIcon from '@mui/icons-material/Shield';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

const AMBER = '#F59E0B';
const DARK_BG = '#0a0e1a';
const CARD_BG = 'rgba(255,255,255,0.04)';
const CARD_BORDER = 'rgba(255,255,255,0.08)';
const MUTED = 'rgba(255,255,255,0.6)';

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [email, setEmail] = useState('');
  const [submitState, setSubmitState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [submitMsg, setSubmitMsg] = useState('');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitState('loading');
    try {
      const { data } = await apiClient.post('/api/newsletter/subscribe', { email: email.trim(), source: 'landing' });
      setSubmitState('success');
      setSubmitMsg(data.message);
      setEmail('');
    } catch (err: any) {
      setSubmitState('error');
      setSubmitMsg(err.message || 'Something went wrong');
    }
  };

  return (
    <Box sx={{ bgcolor: DARK_BG, color: '#fff', minHeight: '100vh' }}>

      {/* ─── Navbar ─── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 2, sm: 6 }, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <img src={logo} alt="" width={36} height={36} />
          <Box
            component="img"
            src="/images/logo.png"
            alt="Pryvo"
            sx={{ height: 32, width: 'auto', display: 'block' }}
          />
        </Box>
        {currentUser ? (
          <Button
            variant="outlined"
            onClick={() => navigate('/discover')}
            sx={{
              color: AMBER,
              borderColor: 'rgba(245,158,11,0.4)',
              '&:hover': { borderColor: AMBER, bgcolor: 'rgba(245,158,11,0.08)' },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Open App
          </Button>
        ) : (
          <Button
            variant="outlined"
            onClick={() => navigate('/login')}
            sx={{
              color: AMBER,
              borderColor: 'rgba(245,158,11,0.4)',
              '&:hover': { borderColor: AMBER, bgcolor: 'rgba(245,158,11,0.08)' },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Sign in
          </Button>
        )}
      </Box>

      {/* ─── Hero ─── */}
      <Container maxWidth="md" sx={{ textAlign: 'center', pt: { xs: 8, sm: 14 }, pb: { xs: 6, sm: 10 } }}>
        <Box
          component="img"
          src="/images/logo.png"
          alt="Pryvo"
          sx={{
            width: { xs: 160, sm: 200 },
            height: 'auto',
            display: 'block',
            mx: 'auto',
            mb: 4,
          }}
        />
        <Box sx={{
          display: 'inline-block',
          px: 2, py: 0.5, mb: 4,
          borderRadius: 999,
          bgcolor: 'rgba(245,158,11,0.08)',
          border: `1px solid rgba(245,158,11,0.2)`,
        }}>
          <Typography variant="caption" sx={{ color: AMBER, fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
            Hedge fund-grade intelligence · built for retail
          </Typography>
        </Box>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 800,
            letterSpacing: '-1px',
            lineHeight: 1.15,
            fontSize: { xs: '2.2rem', sm: '3.2rem', md: '3.8rem' },
            mb: 3,
          }}
        >
          Pry into volatility. Find your edge.
        </Typography>
        <Typography
          variant="h6"
          sx={{ color: MUTED, maxWidth: 560, mx: 'auto', mb: 5, fontWeight: 400, lineHeight: 1.6 }}
        >
          AI-graded earnings events, conviction-ranked wheel plays, and real market-maker positioning — for self-directed investors.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate(currentUser ? '/discover' : '/login')}
          sx={{
            bgcolor: AMBER,
            color: '#000',
            fontWeight: 700,
            px: 5,
            py: 1.5,
            fontSize: '1.05rem',
            textTransform: 'none',
            '&:hover': { bgcolor: '#d97706' },
          }}
        >
          {currentUser ? 'Open App' : 'Get started free'}
        </Button>
      </Container>

      {/* ─── Earnings Intelligence Showcase ─── */}
      <Box sx={{
        background: `linear-gradient(180deg, rgba(245,158,11,0.04) 0%, rgba(245,158,11,0) 100%)`,
        borderTop: `1px solid ${CARD_BORDER}`,
        borderBottom: `1px solid ${CARD_BORDER}`,
        py: { xs: 8, sm: 12 },
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2, py: 0.75, mb: 3,
              borderRadius: 999,
              bgcolor: 'rgba(245,158,11,0.1)',
              border: `1px solid rgba(245,158,11,0.3)`,
            }}>
              <AutoAwesomeIcon sx={{ fontSize: 16, color: AMBER }} />
              <Typography variant="caption" sx={{ color: AMBER, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                The Pryvo Flagship
              </Typography>
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 3, letterSpacing: '-0.5px', fontSize: { xs: '2rem', sm: '2.6rem', md: '3rem' } }}>
              The earnings edge, decoded.
            </Typography>
            <Typography sx={{ color: MUTED, maxWidth: 720, mx: 'auto', fontSize: '1.1rem', lineHeight: 1.7 }}>
              Every quarter, 3,000+ companies report earnings. Most are noise. A handful are gold.
              Pryvo uses Claude to grade every upcoming event — <Box component="span" sx={{ color: AMBER, fontWeight: 700 }}>A, B, C, or skip</Box> —
              so you know where to position, where to hedge, and where to sit on your hands.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {[
              {
                icon: <EventNoteIcon sx={{ fontSize: 32, color: AMBER }} />,
                title: 'AI-graded events',
                desc: 'Claude analyzes historical IV behavior, beat/miss patterns, analyst revision trends, and setup quality. Every event lands in your inbox with a conviction grade before the bell.',
              },
              {
                icon: <PsychologyIcon sx={{ fontSize: 32, color: AMBER }} />,
                title: 'Strategy recommendations',
                desc: 'Pryvo picks the right play for each grade — credit spread, naked short, iron condor, or stay out. Risk-adjusted, math-backed, no hype.',
              },
              {
                icon: <InsightsIcon sx={{ fontSize: 32, color: AMBER }} />,
                title: 'Post-earnings drift',
                desc: 'See how stocks historically moved after beats versus misses for each ticker. Position for the drift, not the coin flip.',
              },
            ].map((f, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Paper sx={{
                  p: 4, height: '100%',
                  bgcolor: 'rgba(245,158,11,0.04)',
                  border: `1px solid rgba(245,158,11,0.2)`,
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: AMBER,
                    bgcolor: 'rgba(245,158,11,0.06)',
                    transform: 'translateY(-2px)',
                  },
                }}>
                  <Box sx={{ mb: 2.5 }}>{f.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>{f.title}</Typography>
                  <Typography variant="body2" sx={{ color: MUTED, lineHeight: 1.7 }}>{f.desc}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate(currentUser ? '/discover' : '/login')}
              sx={{
                color: AMBER,
                borderColor: AMBER,
                fontWeight: 700,
                px: 4,
                py: 1.25,
                textTransform: 'none',
                '&:hover': { borderColor: AMBER, bgcolor: 'rgba(245,158,11,0.08)' },
              }}
            >
              See the earnings calendar →
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ─── Anti-positioning ─── */}
      <Box sx={{ bgcolor: 'rgba(255,255,255,0.02)', py: { xs: 6, sm: 10 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            Built for investors, not traders
          </Typography>
          <Typography sx={{ color: MUTED, maxWidth: 600, mx: 'auto', mb: 6, lineHeight: 1.7 }}>
            Other platforms want you to make 5,000 trades a year. Pryvo is for people
            who'd rather make 50 thoughtful ones — and spend the rest of their time
            living their life.
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            {[
              { left: 'Trade often, trade fast', right: 'Trade deliberately, sleep soundly' },
              { left: 'Guess at earnings setups', right: 'AI-graded earnings conviction' },
              { left: 'Chase volatility across random tickers', right: 'Focus on stocks you believe in' },
              { left: 'Requires full-time attention', right: 'Designed for people with day jobs' },
            ].map((row, i) => (
              <Grid item xs={12} key={i}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Paper sx={{
                    flex: 1, minWidth: 220, maxWidth: 280, px: 3, py: 2,
                    bgcolor: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)',
                    textDecoration: 'line-through', color: 'rgba(255,255,255,0.4)',
                  }}>
                    <Typography variant="body2">{row.left}</Typography>
                  </Paper>
                  <Paper sx={{
                    flex: 1, minWidth: 220, maxWidth: 280, px: 3, py: 2,
                    bgcolor: 'rgba(245,158,11,0.06)', border: `1px solid rgba(245,158,11,0.2)`,
                    color: '#fff',
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.right}</Typography>
                  </Paper>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ─── Features ─── */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 10 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 2 }}>
          Everything you need. Nothing you don't.
        </Typography>
        <Typography sx={{ color: MUTED, textAlign: 'center', maxWidth: 560, mx: 'auto', mb: 6, lineHeight: 1.7 }}>
          The full Pryvo toolkit — six signals that move in sync.
        </Typography>
        <Grid container spacing={4}>
          {[
            {
              icon: <PsychologyIcon sx={{ fontSize: 36, color: AMBER }} />,
              title: 'Earnings Intelligence',
              desc: 'AI grades every upcoming earnings event so you know which ones to trade, which to hedge, and which to avoid entirely.',
            },
            {
              icon: <TrendingUpIcon sx={{ fontSize: 36, color: AMBER }} />,
              title: 'Wheel Strategy Discovery',
              desc: 'Find the best cash-secured puts and covered calls across your watchlist, ranked by annualized return.',
            },
            {
              icon: <VisibilityIcon sx={{ fontSize: 36, color: AMBER }} />,
              title: 'Gamma Exposure (GEX)',
              desc: 'See where market makers are positioned. Understand the invisible forces moving your stocks before you act.',
            },
            {
              icon: <ShieldIcon sx={{ fontSize: 36, color: AMBER }} />,
              title: 'Risk-Aware Grading',
              desc: 'Every opportunity comes with a grade based on IV edge, liquidity, and fundamentals. No guesswork.',
            },
            {
              icon: <AccessTimeIcon sx={{ fontSize: 36, color: AMBER }} />,
              title: 'Max Pain & Expiry Intel',
              desc: 'Know where options expire worthless. Align your strike selection with the path of least resistance.',
            },
            {
              icon: <AutoGraphIcon sx={{ fontSize: 36, color: AMBER }} />,
              title: 'Track Record',
              desc: 'See your real performance over time — annualized APR, win rate, average days in trade. No fake backtests.',
            },
          ].map((f, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Paper sx={{
                p: 3, height: '100%',
                bgcolor: CARD_BG, border: `1px solid ${CARD_BORDER}`,
                transition: 'border-color 0.2s',
                '&:hover': { borderColor: 'rgba(245,158,11,0.3)' },
              }}>
                <Box sx={{ mb: 2 }}>{f.icon}</Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{f.title}</Typography>
                <Typography variant="body2" sx={{ color: MUTED, lineHeight: 1.6 }}>{f.desc}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ─── How it works ─── */}
      <Box sx={{ bgcolor: 'rgba(255,255,255,0.02)', py: { xs: 6, sm: 10 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 6 }}>
            How it works
          </Typography>
          <Grid container spacing={4}>
            {[
              { step: '1', title: 'Scan the market with AI', desc: 'Pryvo grades every upcoming earnings event and surfaces the best wheel opportunities on stocks you already believe in.' },
              { step: '2', title: 'Trade with conviction', desc: 'Take positions only where the math, the timing, and the AI agree. Skip the noise, act on the signal.' },
              { step: '3', title: 'Compound steadily', desc: 'Track your real P&L over time. Earn premium, let winners compound, repeat — without the stress of active trading.' },
            ].map((s, i) => (
              <Grid item xs={12} sm={4} key={i}>
                <Typography variant="h2" sx={{ color: AMBER, fontWeight: 800, opacity: 0.3, mb: 1 }}>
                  {s.step}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{s.title}</Typography>
                <Typography variant="body2" sx={{ color: MUTED, lineHeight: 1.6 }}>{s.desc}</Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ─── Newsletter Signup ─── */}
      <Box sx={{ bgcolor: 'rgba(245,158,11,0.04)', py: { xs: 6, sm: 10 }, borderTop: `1px solid ${CARD_BORDER}`, borderBottom: `1px solid ${CARD_BORDER}` }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <NotificationsActiveIcon sx={{ fontSize: 48, color: AMBER, mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            The Pryvo Signal Brief
          </Typography>
          <Typography sx={{ color: MUTED, mb: 4, lineHeight: 1.7 }}>
            Every morning before the bell, Pryvo scans the options market, grades upcoming
            earnings events, and ranks the highest-conviction plays. Free, daily, in your inbox.
          </Typography>
          {submitState === 'success' ? (
            <Alert severity="success" sx={{ maxWidth: 440, mx: 'auto' }}>{submitMsg}</Alert>
          ) : (
            <Box
              component="form"
              onSubmit={handleSubscribe}
              sx={{ display: 'flex', gap: 1.5, maxWidth: 440, mx: 'auto', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
            >
              <TextField
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setSubmitState('idle'); }}
                required
                size="small"
                sx={{
                  flex: 1,
                  minWidth: 200,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255,255,255,0.06)',
                    color: '#fff',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                    '&:hover fieldset': { borderColor: 'rgba(245,158,11,0.4)' },
                    '&.Mui-focused fieldset': { borderColor: AMBER },
                  },
                  '& input::placeholder': { color: 'rgba(255,255,255,0.35)' },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={submitState === 'loading'}
                sx={{
                  bgcolor: AMBER,
                  color: '#000',
                  fontWeight: 700,
                  px: 4,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': { bgcolor: '#d97706' },
                }}
              >
                {submitState === 'loading' ? 'Joining...' : 'Subscribe'}
              </Button>
            </Box>
          )}
          {submitState === 'error' && (
            <Alert severity="error" sx={{ maxWidth: 440, mx: 'auto', mt: 2 }}>{submitMsg}</Alert>
          )}
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block', mt: 2 }}>
            No spam. Unsubscribe anytime. Just signal.
          </Typography>
        </Container>
      </Box>

      {/* ─── Final CTA ─── */}
      <Container maxWidth="sm" sx={{ textAlign: 'center', py: { xs: 8, sm: 12 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
          Stop guessing. Start prying.
        </Typography>
        <Typography sx={{ color: MUTED, mb: 4, lineHeight: 1.7 }}>
          Join self-directed investors using Pryvo to turn volatility
          into a steady, patient edge.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate(currentUser ? '/discover' : '/login')}
          sx={{
            bgcolor: AMBER,
            color: '#000',
            fontWeight: 700,
            px: 5,
            py: 1.5,
            fontSize: '1.05rem',
            textTransform: 'none',
            '&:hover': { bgcolor: '#d97706' },
          }}
        >
          {currentUser ? 'Open App' : 'Get started free'}
        </Button>
      </Container>

      {/* ─── Footer ─── */}
      <Box sx={{ borderTop: `1px solid ${CARD_BORDER}`, py: 3, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)', mb: 1 }}>
          Pryvo is an analytical tool for self-directed investors. Not financial advice. Options involve risk.
        </Typography>
        <Typography
          variant="body2"
          component="a"
          href="/terms"
          sx={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'underline', '&:hover': { color: 'rgba(255,255,255,0.6)' } }}
        >
          Terms & Conditions
        </Typography>
      </Box>
    </Box>
  );
};

export default Landing;
